import React, { useCallback, useEffect } from 'react';
import useIsAdminHubAIEnabled from '@atlassian/jira-platform-react-hooks-use-is-admin-hub-ai-enabled/src/controllers/index.tsx';
import type ServicedeskKnowledgeHubSuggestedArticlesType from '@atlassian/jira-spa-apps-servicedesk-knowledge-hub-suggested-articles/src/index.tsx';
import { markAsProductBundleReady } from '@atlassian/jira-spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@atlassian/jira-spa/src/view/lazy-page/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { useResource } from '@atlassian/react-resource-router';
import { kbTopicsResource } from '@atlassian/jira-router-resources-knowledge-hub/src/services/kb-topics/index.tsx';
import { useSuggestedTopics } from '@atlassian/jira-servicedesk-knowledge-performance/src/controllers/store/index.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

export const LazyServicedeskKnowledgeHubSuggestedArticles = lazyForPaint<
	typeof ServicedeskKnowledgeHubSuggestedArticlesType
>(() =>
	markAsProductBundleReady(
		import(
			/* webpackChunkName: "async-servicedesk-knowledge-hub-suggested-articles", jiraSpaEntry: "async-servicedesk-knowledge-hub-suggested-articles" */ '@atlassian/jira-spa-apps-servicedesk-knowledge-hub-suggested-articles'
		),
	),
);

export const ServiceDeskKnowledgeHubSuggestedArticles = (): React.JSX.Element => {
	const {
		data: isAdminHubAIEnabled,
		loading: isAdminHubAIEnabledLoading,
		error: isAdminHubAIEnabledError,
		fetchAIEnablement,
	} = useIsAdminHubAIEnabled('jira-servicedesk');

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ data: pageContextData }] = usePageContext();
	const { projectContext, tenantContext } = pageContextData || {};
	const { projectId } = projectContext || {};
	const activationId = tenantContext?.activationId || '';

	const [{ error: topicsError, loading: isTopicsLoading }, { fetchSuggestedTopics }] =
		useSuggestedTopics();
	const appEditions = useAppEditions();

	const isTopicSuggestionAllowed = useCallback(() => {
		return appEditions.serviceDesk === 'PREMIUM_EDITION';
	}, [appEditions.serviceDesk]);

	const showSuggestedTopics =
		isTopicSuggestionAllowed() &&
		!isAdminHubAIEnabledLoading &&
		!isAdminHubAIEnabledError &&
		isAdminHubAIEnabled &&
		!isTopicsLoading &&
		!topicsError;

	useEffect(() => {
		projectId &&
			isTopicSuggestionAllowed() &&
			isAdminHubAIEnabled &&
			fetchSuggestedTopics(projectId, createAnalyticsEvent({}), activationId);
	}, [
		activationId,
		createAnalyticsEvent,
		fetchSuggestedTopics,
		isTopicSuggestionAllowed,
		projectId,
		isAdminHubAIEnabled,
	]);

	useEffect(() => {
		isTopicSuggestionAllowed() && fetchAIEnablement();
	}, [fetchAIEnablement, isTopicSuggestionAllowed]);

	return (
		<>
			{showSuggestedTopics && (
				<LazyPage
					Page={LazyServicedeskKnowledgeHubSuggestedArticles}
					pageId="spa-apps/servicedesk/knowledge-hub/suggested-articles"
					shouldShowSpinner
				/>
			)}
		</>
	);
};

export const ServiceDeskKnowledgeHubSuggestedArticlesWithResource = (): React.JSX.Element => {
	const {
		data: isAdminHubAIEnabled,
		loading: isAdminHubAIEnabledLoading,
		error: isAdminHubAIEnabledError,
		fetchAIEnablement,
	} = useIsAdminHubAIEnabled('jira-servicedesk');

	const {
		data: topicsData,
		error: topicsError,
		loading: isTopicsLoading,
	} = useResource(kbTopicsResource);

	const showSuggestedTopics =
		topicsData?.topicSuggestionAllowed &&
		!isAdminHubAIEnabledLoading &&
		!isAdminHubAIEnabledError &&
		isAdminHubAIEnabled &&
		!isTopicsLoading &&
		!topicsError;

	useEffect(() => {
		topicsData?.topicSuggestionAllowed && fetchAIEnablement();
	}, [fetchAIEnablement, topicsData?.topicSuggestionAllowed]);

	return (
		<>
			{showSuggestedTopics && (
				<LazyPage
					Page={LazyServicedeskKnowledgeHubSuggestedArticles}
					pageId="spa-apps/servicedesk/knowledge-hub/suggested-articles"
					shouldShowSpinner
				/>
			)}
		</>
	);
};
